'use client';

import { LocaleLink } from '@/core/i18n';
import {
  ShieldCheck,
  Lock,
  Server,
  Eye,
  KeyRound,
  Globe,
  AlertTriangle,
  FileCheck,
  Users,
  Clock,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';
import { usePageContent } from '@/core/i18n/content';
import { SECURITY_CONTENT } from '@/core/i18n/content/security.content';

/* -------------------------------------------------------------------------- */
/*  Helper sub-components                                                     */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*  Section component                                                         */
/* -------------------------------------------------------------------------- */

function SectionHeading({ icon: Icon, title, id }: { icon: typeof ShieldCheck; title: string; id: string }) {
  return (
    <h2 id={id} className="flex items-center gap-3 text-xl font-semibold text-hms-text mb-6">
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-hms-gold/10 border border-hms-gold/20">
        <Icon className="h-5 w-5 text-hms-gold" />
      </span>
      {title}
    </h2>
  );
}

function FeatureCard({ title, desc, id }: { title: string; desc: string; id: string }) {
  return (
    <div id={id} className="rounded-xl border border-hms-border bg-hms-card p-5 space-y-2 hover:border-hms-gold/30 transition-colors">
      <div className="flex items-center gap-2">
        <CheckCircle className="h-4 w-4 text-hms-green flex-shrink-0" />
        <h3 className="text-sm font-semibold text-hms-text">{title}</h3>
      </div>
      <p className="text-sm text-hms-textSub leading-relaxed pl-6">{desc}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  SecurityPage                                                              */
/* -------------------------------------------------------------------------- */

export function SecurityPage() {
  const c = usePageContent(SECURITY_CONTENT);

  return (
    <div id="security-page" className="min-h-screen bg-hms-bg">
      {/* ── Hero Section ── */}
      <section id="security-hero" className="relative overflow-hidden border-b border-hms-border">
        <div className="absolute inset-0 bg-gradient-to-br from-hms-gold/5 via-transparent to-hms-teal/5" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-hms-gold/30 bg-hms-gold/10 px-4 py-1.5 text-xs font-medium text-hms-gold mb-6">
            <ShieldCheck className="h-3.5 w-3.5" />
            {c.hero.badge}
          </span>
          <h1 id="security-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-hms-text leading-tight">
            {c.hero.title}
            <span className="block text-hms-gold mt-1">{c.hero.titleAccent}</span>
          </h1>
          <p id="security-subtitle" className="mt-5 text-base sm:text-lg text-hms-textSub max-w-2xl mx-auto leading-relaxed">
            {c.hero.subtitle}
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">
        {/* ── Compliance Certifications ── */}
        <section id="security-compliance">
          <SectionHeading icon={FileCheck} title={c.sections.compliance} id="security-compliance-heading" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.certifications.map((cert) => (
              <div
                key={cert.name}
                id={`security-cert-${cert.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="rounded-xl border border-hms-border bg-hms-card p-5 text-center space-y-2 hover:border-hms-gold/30 transition-colors"
              >
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-hms-green/10 border border-hms-green/20 mx-auto">
                  <ShieldCheck className="h-4 w-4 text-hms-green" />
                </div>
                <h3 className="text-sm font-semibold text-hms-text">{cert.name}</h3>
                <p className="text-xs text-hms-textSub leading-relaxed">{cert.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Data Protection ── */}
        <section id="security-data-protection">
          <SectionHeading icon={Lock} title={c.sections.dataProtection} id="security-data-protection-heading" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {c.dataProtection.map((item) => (
              <FeatureCard
                key={item.title}
                id={`security-dp-${item.title.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
                title={item.title}
                desc={item.desc}
              />
            ))}
          </div>
        </section>

        {/* ── Infrastructure ── */}
        <section id="security-infrastructure">
          <SectionHeading icon={Server} title={c.sections.infrastructure} id="security-infrastructure-heading" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {c.infrastructure.map((item) => (
              <FeatureCard
                key={item.title}
                id={`security-infra-${item.title.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
                title={item.title}
                desc={item.desc}
              />
            ))}
          </div>
        </section>

        {/* ── Access Control ── */}
        <section id="security-access-control">
          <SectionHeading icon={KeyRound} title={c.sections.accessControl} id="security-access-heading" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {c.accessControl.map((item) => (
              <FeatureCard
                key={item.title}
                id={`security-ac-${item.title.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
                title={item.title}
                desc={item.desc}
              />
            ))}
          </div>
        </section>

        {/* ── Penetration Testing ── */}
        <section id="security-pentesting">
          <SectionHeading icon={Eye} title={c.sections.pentesting} id="security-pentest-heading" />
          <div className="rounded-xl border border-hms-border bg-hms-card p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {c.pentesting.map((item, idx) => (
                <div key={idx} id={`security-pentest-${['annual', 'bounty', 'scanning', 'soc'][idx] ?? idx}`} className="flex items-start gap-3">
                  <CheckCircle className="h-4 w-4 text-hms-green mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-hms-text">{item.title}</h3>
                    <p className="text-xs text-hms-textSub mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Data Residency ── */}
        <section id="security-data-residency">
          <SectionHeading icon={Globe} title={c.sections.dataResidency} id="security-residency-heading" />
          <p className="text-sm text-hms-textSub mb-4 -mt-4">
            {c.sections.dataResidencyDesc}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {c.regions.map((r) => (
              <div
                key={r.region}
                id={`security-region-${r.region.toLowerCase().replace(/\s+/g, '-')}`}
                className="rounded-xl border border-hms-border bg-hms-card p-5 space-y-2 hover:border-hms-gold/30 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-hms-teal flex-shrink-0" />
                  <h3 className="text-sm font-semibold text-hms-text">{r.region}</h3>
                </div>
                <p className="text-xs text-hms-textSub leading-relaxed pl-6">{r.locations}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Incident Response ── */}
        <section id="security-incident-response">
          <SectionHeading icon={AlertTriangle} title={c.sections.incidentResponse} id="security-incident-heading" />
          <div className="rounded-xl border border-hms-border bg-hms-card p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div id="security-ir-soc" className="text-center space-y-2">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-hms-teal/10 border border-hms-teal/20 mx-auto">
                  <Users className="h-5 w-5 text-hms-teal" />
                </div>
                <h3 className="text-sm font-semibold text-hms-text">{c.incidentResponse[0]?.title}</h3>
                <p className="text-xs text-hms-textSub">{c.incidentResponse[0]?.desc}</p>
              </div>
              <div id="security-ir-response" className="text-center space-y-2">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-hms-red/10 border border-hms-red/20 mx-auto">
                  <Clock className="h-5 w-5 text-hms-red" />
                </div>
                <h3 className="text-sm font-semibold text-hms-text">{c.incidentResponse[1]?.title}</h3>
                <p className="text-xs text-hms-textSub">{c.incidentResponse[1]?.desc}</p>
              </div>
              <div id="security-ir-comms" className="text-center space-y-2">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-hms-blue/10 border border-hms-blue/20 mx-auto">
                  <ShieldCheck className="h-5 w-5 text-hms-blue" />
                </div>
                <h3 className="text-sm font-semibold text-hms-text">{c.incidentResponse[2]?.title}</h3>
                <p className="text-xs text-hms-textSub">{c.incidentResponse[2]?.desc}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section id="security-cta" className="rounded-xl border border-hms-gold/20 bg-gradient-to-r from-hms-gold/5 to-hms-teal/5 p-8 text-center space-y-4">
          <h2 className="text-xl font-semibold text-hms-text">{c.cta.title}</h2>
          <p className="text-sm text-hms-textSub max-w-lg mx-auto">
            {c.cta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <LocaleLink
              id="security-cta-contact"
              href="/contact"
              className="inline-flex items-center gap-2 rounded-lg bg-hms-gold px-6 py-2.5 text-sm font-semibold text-white hover:bg-hms-gold/90 transition-colors"
            >
              {c.cta.contactBtn}
              <ArrowRight className="h-4 w-4" />
            </LocaleLink>
            <a
              id="security-cta-email"
              href="mailto:security@hotelstack.io"
              className="inline-flex items-center gap-2 rounded-lg border border-hms-border bg-hms-card px-6 py-2.5 text-sm font-medium text-hms-textSub hover:text-hms-text hover:border-hms-gold/30 transition-colors"
            >
              security@hotelstack.io
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
